import { format, isSameDay, isToday, isYesterday } from "date-fns"
import { TFunction } from "i18next"
import { Clipboard, Folder, Grid, Home, MessageSquare, User, Users } from "react-feather"
import { ChatType } from "../api/chat-response"

type ChatIconProps = { size?: string | number }

export const getSubtitle = (subtitle: string, t: TFunction) => {
  if (subtitle === "org") {
    return t("chatTypes.org")
  } else if (subtitle === "project") {
    return t("chatTypes.project")
  } else if (subtitle === "task") {
    return t("chatTypes.task")
  } else if (subtitle === "dm") {
    return t("chatTypes.dm")
  } else if (subtitle === "group") {
    return t("chatTypes.group")
  } else if (subtitle === "workspace") {
    return t("chatTypes.workspace")
  } else {
    return subtitle
  }
}

export type ChatFilter = "all" | "unread" | "dm" | "group" | "task" | "project" | "space"

type ChatLike = { type: ChatType; unreadCount: number }

export const CHAT_FILTERS = [
  { value: "all", labelKey: "chatFilters.all" },
  { value: "unread", labelKey: "chatFilters.unread" },
  { value: "dm", labelKey: "chatFilters.dm" },
  { value: "group", labelKey: "chatFilters.group" },
  { value: "task", labelKey: "chatFilters.task" },
  { value: "project", labelKey: "chatFilters.project" },
  { value: "space", labelKey: "chatFilters.space" },
] as const

// "space" is the one filter that is not a chat type: it covers the rooms that come with a
// container (the org, a workspace) rather than with the people in them.
export const matchesChatFilter = (chat: ChatLike, filter: ChatFilter) => {
  if (filter === "all") return true
  if (filter === "unread") return chat.unreadCount > 0
  if (filter === "space") return chat.type === "org" || chat.type === "workspace"

  return chat.type === filter
}

export const getChatIcon = (type: string): React.ComponentType<ChatIconProps> => {
  switch (type) {
    case "org":
      return Home
    case "project":
      return Folder
    case "task":
      return Clipboard
    case "workspace":
      return Grid
    case "group":
      return Users
    case "dm":
      return User
    default:
      return MessageSquare
  }
}

export const getDayLabel = (createdAt: string, t: TFunction) => {
  const date = new Date(createdAt)

  if (isToday(date)) return t("dayLabel.today")
  if (isYesterday(date)) return t("dayLabel.yesterday")

  return format(date, "EEEE, d MMMM yyyy")
}

export const groupByDay = <T extends { createdAt: string }>(items: T[]) => {
  const groups: { day: string; messages: T[] }[] = []

  items.forEach((item) => {
    const current = groups[groups.length - 1]

    if (current && isSameDay(new Date(current.day), new Date(item.createdAt))) current.messages.push(item)
    else groups.push({ day: item.createdAt, messages: [item] })
  })

  return groups
}
